import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation, useParams } from "react-router";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step
} from "react-joyride";

import { ProjectContext } from "context/ProjectProvider";
import { TourContext, TourContextValue } from "./TourContext";
import { findTourForRoute, getTour } from "./registry";
import { tourLocale, tourStyles } from "./joyride-styles";
import { TourConfig, TourId, TourStep, TourStepContext } from "./types";

import "./tours";

const SELECTOR_WAIT_MS = 5000;

// react-floater's default floater style is `position: relative`, which is
// in-flow. On every step transition the old floater portal is removed and
// a new one is appended to <body>; for the few ms before Popper.js inline-
// styles `position: absolute`, the relative-positioned tooltip's content
// height is added to body's flow and a window scrollbar appears for one
// frame. Forcing `position: absolute` from the initial render keeps the
// floater out of flow throughout the transition. Popper's later inline
// override is then a no-op on position, and its viewport-relative
// transform math is unaffected because the portal div itself is still
// `position: static`.
const floaterProps = {
  disableAnimation: true,
  styles: {
    floater: { position: "absolute" as const }
  }
};

function waitForSelectors(
  selectors: string[],
  timeoutMs: number
): Promise<boolean> {
  if (selectors.every((s) => document.querySelector(s))) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let resolved = false;
    const finish = (ok: boolean) => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(ok);
    };

    const observer = new MutationObserver(() => {
      if (selectors.every((s) => document.querySelector(s))) finish(true);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour-ready"]
    });

    const timer = setTimeout(() => finish(false), timeoutMs);
  });
}

function resolveStepContent(step: TourStep, ctx: TourStepContext): ReactNode {
  return typeof step.content === "function" ? step.content(ctx) : step.content;
}

function buildJoyrideSteps(tour: TourConfig, ctx: TourStepContext): Step[] {
  return tour.steps
    .filter((step) => !step.optional || document.querySelector(step.target))
    .map((step) => ({
      target: step.target,
      title: step.title,
      content: resolveStepContent(step, ctx),
      placement: step.placement,
      disableBeacon: step.disableBeacon ?? true
    }));
}

interface TourProviderProps {
  children: ReactNode;
}

function TourProvider({ children }: TourProviderProps) {
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();
  const { tokenContents, isFeatureFlagEnabled } = useContext(ProjectContext);
  const role = tokenContents?.role;

  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Tracks "the tour has already fired on this pathname visit". Reset to
  // null when navigating to a non-tour route (in the `!matched` branch),
  // so the next return to the dashboard fires a fresh tour. Without this,
  // Skip/Done/Close would let the tour immediately re-open on the same page.
  const startedRef = useRef<string | null>(null);

  const startTour = useCallback((id: TourId) => {
    if (!getTour(id)) return;
    setActiveTourId(id);
    setStepIndex(0);
    setRun(true);
  }, []);

  const stopTour = useCallback(() => {
    setRun(false);
    setActiveTourId(null);
    setStepIndex(0);
  }, []);

  const markCompleted = useCallback<TourContextValue["markCompleted"]>(() => {
    // Persistence is intentionally deferred. localStorage gating will hook in here later.
  }, []);

  const isCompleted = useCallback<TourContextValue["isCompleted"]>(
    () => false,
    []
  );

  const resetTour = useCallback<TourContextValue["resetTour"]>(() => {
    // No-op until persistence lands.
  }, []);

  useEffect(() => {
    if (!role) return;
    const matched = findTourForRoute(pathname, role);
    if (!matched) {
      if (activeTourId) stopTour();
      startedRef.current = null;
      return;
    }

    if (
      matched.projectIdAllowlist &&
      (!projectId || !matched.projectIdAllowlist.includes(projectId))
    )
      return;

    const startKey = `${pathname}::${matched.id}`;
    if (startedRef.current === startKey) return;

    const requiredSelectors = matched.steps
      .filter((s) => !s.optional)
      .map((s) => s.target);
    if (matched.readySelector) requiredSelectors.push(matched.readySelector);

    let cancelled = false;
    waitForSelectors(requiredSelectors, SELECTOR_WAIT_MS).then((ok) => {
      if (cancelled || !ok) return;
      if (startedRef.current === startKey) return;
      startedRef.current = startKey;
      startTour(matched.id);
    });

    return () => {
      cancelled = true;
    };
  }, [
    pathname,
    role,
    projectId,
    activeTourId,
    startTour,
    stopTour,
    isFeatureFlagEnabled
  ]);

  // Joyride 2.9.x in controlled mode (with a managed `stepIndex`) calls
  // `helpers.close()` on the close X, which only flips the internal action to
  // CLOSE without changing status — so no callback fires and our parent never
  // hears about it. Listen for clicks on the close button at the document
  // level and stop the tour ourselves.
  useEffect(() => {
    if (!run) return undefined;
    const handler = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-test-id="button-close"]')
      ) {
        if (activeTourId) markCompleted(activeTourId);
        stopTour();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [run, activeTourId, markCompleted, stopTour]);

  // While the tour is running, lock the window scrollbar. Joyride/react-floater
  // and Popper interact in subtle ways during step transitions that briefly
  // extend body's flow height (the floater's inline `position` gets cleared
  // when Popper destroys, the new portal mounts before its child becomes
  // absolute, etc). Targeted fixes haven't proven reliable across every
  // transition. The dashboard layout uses h-screen so the body never
  // legitimately needs to scroll while the tour is on this route — locking
  // the documentElement's overflow guarantees no flash regardless of which
  // mechanism momentarily extends body height.
  useEffect(() => {
    if (!run) return undefined;
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = previousOverflow;
    };
  }, [run]);

  const activeTour = activeTourId ? getTour(activeTourId) : undefined;
  const stepContextHook = activeTour?.useStepContext;

  const defaultStepContext: TourStepContext = useMemo(
    () => ({ projectId: projectId ?? "", userRole: role }),
    [projectId, role]
  );

  const stepContext = stepContextHook ? stepContextHook() : defaultStepContext;

  const joyrideSteps = useMemo<Step[]>(() => {
    if (!activeTour) return [];
    return buildJoyrideSteps(activeTour, stepContext);
  }, [activeTour, stepContext]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data;
      if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
        setStepIndex(Math.max(0, index - 1));
      } else if (type === EVENTS.TARGET_NOT_FOUND) {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (activeTourId) markCompleted(activeTourId);
        stopTour();
      }
    },
    [activeTourId, markCompleted, stopTour]
  );

  const value = useMemo<TourContextValue>(
    () => ({
      startTour,
      stopTour,
      resetTour,
      markCompleted,
      isCompleted,
      activeTourId
    }),
    [startTour, stopTour, resetTour, markCompleted, isCompleted, activeTourId]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={joyrideSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        scrollOffset={80}
        scrollDuration={0}
        disableScrollParentFix
        spotlightClicks={false}
        disableOverlayClose
        disableScrolling={false}
        styles={tourStyles}
        locale={tourLocale}
        floaterProps={floaterProps}
        callback={handleCallback}
      />
    </TourContext.Provider>
  );
}

export default TourProvider;
